// src/pages/Login.tsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import { login, clearError } from "../redux/slices/authSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  Shield,
  AlertCircle,
  User,
  Sparkles,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import toast from "react-hot-toast";

const Login: React.FC = () => {
  const [loginInput, setLoginInput] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isEmail, setIsEmail] = useState<boolean | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error, isAuthenticated, user, token } = useSelector(
    (state: RootState) => state.auth,
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      if (!user.email_verified_at) {
        navigate("/verify-email", { state: { email: user.email } });
      } else {
        navigate("/dashboard");
      }
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (token && user && !user.email_verified_at) {
      navigate("/verify-email", { state: { email: user.email } });
    }
  }, [token, user, navigate]);

  const validateInputType = (input: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(input);
  };

  const handleLoginChange = (value: string) => {
    setLoginInput(value);
    if (value) {
      setIsEmail(validateInputType(value));
    } else {
      setIsEmail(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!loginInput || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    if (validateInputType(loginInput) && !loginInput.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    try {
      const result = await dispatch(
        login({
          login: loginInput,
          password,
        }) as any,
      );

      if (login.fulfilled.match(result)) {
        const response = result.payload;

        if (response.requires_verification) {
          toast.success("Verification code sent to your email!");
          navigate("/verify-email", {
            state: {
              email: validateInputType(loginInput)
                ? loginInput
                : response.user.email,
            },
          });
        } else {
          toast.success("Login successful!");
        }
      }
    } catch (error) {
      toast.error("An unexpected error occurred");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 via-gray-100 to-gray-200 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950 p-4">
      <div className="w-full max-w-md">
        {/* Floating Card */}
        <div className="relative">
          {/* Background Glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>

          {/* Main Card */}
          <div className="relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/30 dark:border-gray-700/30">
            {/* Header */}
            <div className="text-center p-6 pb-8">
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <img
                    src={logo}
                    alt="ObeliskRecovery"
                    className="h-10 w-auto"
                  />
                  <div className="absolute -top-1 -right-1 h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                Welcome Back
              </h1>
              <p className="text-gray-500 dark:text-gray-400 mt-2 text-sm">
                Sign in to your secure account
              </p>
            </div>

            {/* Form */}
            <div className="px-6 pb-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Email/Username Input */}
                <div className="group">
                  <label
                    htmlFor="login"
                    className={`${GlobalStyles.inputLabel} mb-2 flex items-center gap-1`}
                  >
                    <span>Email or Username</span>
                    <Sparkles className="h-3 w-3 text-primary/60" />
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      {isEmail === null ? (
                        <User className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      ) : isEmail ? (
                        <Mail className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      ) : (
                        <User className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                      )}
                    </div>
                    <input
                      id="login"
                      name="login"
                      type="text"
                      autoComplete="username"
                      required
                      value={loginInput}
                      onChange={(e) => handleLoginChange(e.target.value)}
                      className={`${GlobalStyles.input} pl-10 focus:pl-11 transition-all duration-300`}
                      placeholder="Enter email or username"
                      disabled={isLoading}
                    />
                    {loginInput && isEmail !== null && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                          {isEmail ? "Email" : "Username"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Password Input */}
                <div className="group">
                  <div className="flex items-center justify-between mb-2">
                    <label
                      htmlFor="password"
                      className={GlobalStyles.inputLabel}
                    >
                      Password
                    </label>
                    <Link
                      to="/forgot-password"
                      className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">
                      <Lock className="h-4 w-4 text-primary group-focus-within:text-primary/80 transition-colors" />
                    </div>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${GlobalStyles.input} pl-10 pr-10 focus:pl-11 transition-all duration-300`}
                      placeholder="Enter your password"
                      disabled={isLoading}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-primary hover:text-primary/70 transition-colors"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonMedium} w-full group/btn`}
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div
                          className={`${GlobalStyles.spinner} w-4 h-4 mr-2 border-2`}
                        ></div>
                        Signing in...
                      </div>
                    ) : (
                      <div className="flex items-center justify-center">
                        <span>Sign In</span>
                        <svg
                          className="w-4 h-4 ml-2 opacity-0 group-hover/btn:opacity-100 transition-all duration-300 transform group-hover/btn:translate-x-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M14 5l7 7m0 0l-7 7m7-7H3"
                          />
                        </svg>
                      </div>
                    )}
                  </button>
                </div>
              </form>

              {/* Divider */}
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200/50 dark:border-gray-700/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-white dark:bg-gray-800/90 text-gray-500 dark:text-gray-400">
                    New to Syvron?
                  </span>
                </div>
              </div>

              {/* Register Link */}
              <div className="flex flex-col gap-3">
                <Link
                  to="/register"
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonOutline} ${GlobalStyles.buttonMedium} w-full justify-center group/register`}
                >
                  <span>Create Account</span>
                  <svg
                    className="w-4 h-4 ml-2 opacity-0 group-hover/register:opacity-100 transition-all duration-300"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                </Link>
              </div>

              {/* Security Badge */}
              <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                <Shield className="h-3 w-3 text-green-500" />
                <span>Bank-level security & encryption</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-6 flex justify-center gap-4">
          <Link
            to="/terms"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
          >
            Terms
          </Link>
          <span className="text-gray-400">•</span>
          <Link
            to="/privacy"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
          >
            Privacy
          </Link>
          <span className="text-gray-400">•</span>
          <Link
            to="/about"
            className="text-xs text-gray-500 dark:text-gray-400 hover:text-primary transition-colors"
          >
            About
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
